/**
 * Waveform encoding for voice messages.
 *
 * A voice note shows its shape before you press play. Computing that on the
 * recipient's device would mean downloading and decoding the whole clip first,
 * so the sender measures it once and ships ~48 bytes alongside the message.
 */
import { fromB64, toB64 } from './crypto/primitives.ts';

/** Enough bars to read the rhythm of speech, small enough to be free. */
export const WAVEFORM_BUCKETS = 48;

/**
 * Reduce raw audio samples to one 0–255 amplitude per bucket.
 *
 * Uses RMS rather than peak: a single click would otherwise flatten every other
 * bar to nothing, which is exactly what makes some waveforms useless.
 */
export function computeWaveform(samples: Float32Array, buckets = WAVEFORM_BUCKETS): Uint8Array {
  const out = new Uint8Array(buckets);
  if (samples.length === 0) return out;

  const perBucket = Math.max(1, Math.floor(samples.length / buckets));
  const levels = new Float32Array(buckets);
  let loudest = 0;

  for (let b = 0; b < buckets; b++) {
    const start = b * perBucket;
    const end = Math.min(start + perBucket, samples.length);
    let sum = 0;
    for (let i = start; i < end; i++) sum += samples[i] * samples[i];
    const rms = end > start ? Math.sqrt(sum / (end - start)) : 0;
    levels[b] = rms;
    if (rms > loudest) loudest = rms;
  }

  // Normalise to the loudest bucket, so a quiet recording still draws a full
  // shape instead of a flat line.
  if (loudest === 0) return out;
  for (let b = 0; b < buckets; b++) {
    // Square root curve: speech is very peaky, and a linear scale makes normal
    // talking look like silence next to one loud syllable.
    out[b] = Math.round(Math.sqrt(levels[b] / loudest) * 255);
  }
  return out;
}

/** Build a waveform from live meter readings, in decibels (as RN reports). */
export function waveformFromDecibels(readings: number[], buckets = WAVEFORM_BUCKETS): Uint8Array {
  const out = new Uint8Array(buckets);
  if (readings.length === 0) return out;

  const FLOOR_DB = -60;
  for (let b = 0; b < buckets; b++) {
    const index = Math.min(readings.length - 1, Math.floor((b / buckets) * readings.length));
    const db = readings[index];
    // Meters report 0 dB as loudest and go negative; clamp and map to 0..255.
    const normalised = Math.max(0, Math.min(1, (db - FLOOR_DB) / -FLOOR_DB));
    out[b] = Math.round(normalised * 255);
  }
  return out;
}

export function encodeWaveform(peaks: Uint8Array): string {
  return toB64(peaks);
}

export function decodeWaveform(encoded: string | undefined, buckets = WAVEFORM_BUCKETS): number[] {
  if (!encoded) return new Array(buckets).fill(0);
  try {
    return [...fromB64(encoded)];
  } catch {
    return new Array(buckets).fill(0);
  }
}

/** mm:ss for a duration in milliseconds. */
export function formatDuration(ms: number | undefined): string {
  const total = Math.max(0, Math.round((ms ?? 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
